import { createFileRoute } from '@tanstack/react-router'
import { Plus, Calendar } from 'lucide-react'
import { useState } from 'react'

import { UrgeButton } from '@/components/common/urge-button'
import { LeaveForm } from '@/components/hr/leave/leave-form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useConfirmDialog } from '@/hooks'
import {
  useLeaves,
  useCancelLeave,
  type LeaveType,
  type LeaveStatus,
  type LeaveListParams,
} from '@/hooks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/hr/leaves/')({
  component: LeaveListPage,
})

const TYPE_LABELS: Record<LeaveType, string> = {
  annual: '年假',
  sick: '病假',
  personal: '事假',
  marriage: '婚假',
  maternity: '产假',
  paternity: '陪产假',
  bereavement: '丧假',
}

const STATUS_LABELS: Record<LeaveStatus, string> = {
  draft: '草稿',
  pending: '待审批',
  approved: '已批准',
  rejected: '已拒绝',
  cancelled: '已取消',
}

const STATUS_COLORS: Record<LeaveStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
}

function LeaveListPage() {
  const [filters, setFilters] = useState<LeaveListParams>({ page: 1, pageSize: 20 })
  const [showCreateForm, setShowCreateForm] = useState(false)

  const { data, isLoading, error } = useLeaves(filters)
  const cancelMutation = useCancelLeave()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleStatusFilter = (status: LeaveStatus | undefined) => {
    setFilters((prev) => ({ ...prev, status, page: 1 }))
  }

  const handleTypeFilter = (type: LeaveType | undefined) => {
    setFilters((prev) => ({ ...prev, type, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  const handleCancel = async (id: string) => {
    const confirmed = await confirmWithPreset(
      'cancel',
      '确定要取消该请假申请吗？'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await cancelMutation.mutateAsync(id)
      } finally {
        setLoading(false)
      }
    }
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  const leaves = data?.data ?? []
  const meta = data?.meta

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">请假管理</h1>
          <p className="text-muted-foreground">查看和管理请假申请</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          申请请假
        </button>
      </div>

      {showCreateForm && (
        <LeaveForm onClose={() => setShowCreateForm(false)} onSuccess={() => setShowCreateForm(false)} />
      )}

      {/* 筛选 */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleStatusFilter(undefined)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              !filters.status ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            全部
          </button>
          {(Object.keys(STATUS_LABELS) as LeaveStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                filters.status === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {STATUS_LABELS[status]}
            </button>
          ))}
        </div>

        <Select
          value={filters.type ?? 'all'}
          onValueChange={(value) => handleTypeFilter(value === 'all' ? undefined : value as LeaveType)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="全部类型" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部类型</SelectItem>
            {(Object.keys(TYPE_LABELS) as LeaveType[]).map((type) => (
              <SelectItem key={type} value={type}>
                {TYPE_LABELS[type]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* 列表 */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : leaves.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <Calendar className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <p className="text-lg font-medium">暂无请假记录</p>
            <p className="text-sm text-muted-foreground">点击上方按钮申请请假</p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium">类型</th>
                <th className="px-4 py-3 text-left text-sm font-medium">开始日期</th>
                <th className="px-4 py-3 text-left text-sm font-medium">结束日期</th>
                <th className="px-4 py-3 text-left text-sm font-medium">天数</th>
                <th className="px-4 py-3 text-left text-sm font-medium">原因</th>
                <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                <th className="px-4 py-3 text-left text-sm font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {leaves.map((leave) => (
                <tr key={leave.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3 text-sm">{TYPE_LABELS[leave.type]}</td>
                  <td className="px-4 py-3 text-sm">{leave.startDate}</td>
                  <td className="px-4 py-3 text-sm">{leave.endDate}</td>
                  <td className="px-4 py-3 text-sm">{leave.days}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                    {leave.reason}
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', STATUS_COLORS[leave.status])}>
                      {STATUS_LABELS[leave.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {leave.status === 'pending' && (
                        <UrgeButton approvalType="leave" approvalId={leave.id} />
                      )}
                      {(leave.status === 'draft' || leave.status === 'pending') && (
                        <button
                          onClick={() => void handleCancel(leave.id)}
                          className="text-sm text-destructive hover:underline"
                        >
                          取消
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 分页 */}
      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            共 {meta.total} 条记录，第 {meta.page}/{meta.totalPages} 页
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(meta.page - 1)}
              disabled={meta.page <= 1}
              className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              上一页
            </button>
            <button
              onClick={() => handlePageChange(meta.page + 1)}
              disabled={meta.page >= meta.totalPages}
              className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              下一页
            </button>
          </div>
        </div>
      )}
      <ConfirmDialog />
    </div>
  )
}
